import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { setAnalyticsUser, trackPageView } from './lib/analytics';
import './styles/pirate-live-fixes.css';

import AuthPage from './pages/auth/AuthPage';
import GoogleAuthCallbackPage from './pages/auth/GoogleAuthCallbackPage';
import PlayerApp from './pages/player/PlayerApp';
import RoulettePage from './pages/player/RoulettePage';
import AdminLayout from './pages/admin/AdminLayout';
import TenantAuthPage from './pages/tenant/TenantAuthPage';
import StaffPortalPage from './pages/staff/StaffPortalPage';
import FinanceAuthPage from './pages/finance/FinanceAuthPage';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import DepositReturnPage from './pages/player/DepositReturnPage';
import ReferralStaffDashboard from './pages/ReferralStaffDashboard';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import InstallPrompt from './components/InstallPrompt';
import TenantManifestLink from './components/TenantManifestLink';

const RESERVED_PATHS = ['auth','app','admin','api','staff','staff-portal','finance','referral-staff','agent'];
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || 'vegaslobby.vip').toLowerCase();

function isAgentHost() {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
  return host === `agent.${PLATFORM_DOMAIN}`;
}

function getTenantHostSlug() {
  if (typeof window === 'undefined') return null;
  const host = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
  if (!host || host === PLATFORM_DOMAIN || host === 'localhost' || host === '127.0.0.1') return null;
  const suffix = `.${PLATFORM_DOMAIN}`;
  if (host.endsWith(suffix)) {
    const slug = host.slice(0, -suffix.length);
    return slug && !RESERVED_PATHS.includes(slug) ? slug : null;
  }
  return null;
}

function VegasLobbyLoading() {
  return (
    <div className="vl-site-loader vl-site-loader--video" role="status" aria-label="Loading Vegas Lobby">
      <video
        className="vl-site-loader__video"
        src="/assets/vegas-lobby-intro.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
      />
      <div className="vl-site-loader__shade" />
    </div>
  );
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading, tenantSlug } = useAuth();
  const location = useLocation();

  if (loading) {
    return <VegasLobbyLoading />;
  }

  const pathMatch = location.pathname.match(/^\/([a-z0-9-]+)\//i);
  const hostTenantSlug = getTenantHostSlug();
  const slug = tenantSlug || hostTenantSlug || (pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) ? pathMatch[1] : null);

  if (!user) return <Navigate to={hostTenantSlug ? '/auth' : (slug ? `/${slug}/auth` : '/auth')} replace />;

  if (requiredRole === 'admin' && !['admin', 'moderator', 'support'].includes(user.role)) {
    // Referral agents get their own dashboard — never let them into /admin
    if (user.role === 'referral_agent') {
      return <Navigate to="/referral-staff" replace />;
    }
    return <Navigate to={hostTenantSlug ? '/app' : (slug ? `/${slug}/app` : '/app')} replace />;
  }

  return children;
}

/**
 * Restricts a route to users whose role is `referral_agent`. Other
 * roles are bounced to the place that actually makes sense for them.
 */
function ReferralRoleGuard({ children }) {
  const { user, tenantSlug } = useAuth();
  const location = useLocation();
  if (!user) return children;   // ProtectedRoute already handles unauthed state
  const pathMatch = location.pathname.match(/^\/([a-z0-9-]+)\//i);
  const hostTenantSlug = getTenantHostSlug();
  const slug = tenantSlug || hostTenantSlug || (pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) ? pathMatch[1] : null);
  if (user.role === 'referral_agent') return children;
  if (['admin', 'moderator', 'support'].includes(user.role)) {
    return <Navigate to={hostTenantSlug ? '/admin' : (slug ? `/${slug}/admin` : '/admin')} replace />;
  }
  return <Navigate to={hostTenantSlug ? '/app' : (slug ? `/${slug}/app` : '/app')} replace />;
}

// Handles bare tenant URL like /morpheus → go straight to auth for now
// (if user already logged in, redirect to their app/admin)
function TenantRootRedirect({ tenantSlugOverride = null }) {
  const params = useParams();
  const tenantSlug = tenantSlugOverride || params.tenantSlug;
  const { user, loading } = useAuth();
  const hostTenantSlug = getTenantHostSlug();

  if (loading) {
    return <VegasLobbyLoading />;
  }

  if (user) {
    if (['admin', 'moderator', 'support'].includes(user.role)) {
      return <Navigate to={hostTenantSlug ? '/admin' : `/${tenantSlug}/admin`} replace />;
    }
    return <Navigate to={hostTenantSlug ? '/app' : `/${tenantSlug}/app`} replace />;
  }

  // Unauthenticated → bypass landing page for now
  return <Navigate to={hostTenantSlug ? '/auth' : `/${tenantSlug}/auth`} replace />;
}

function AppRouter() {
  const hostTenantSlug = getTenantHostSlug();
  const agentHost = isAgentHost();
  return (
    <Routes>
      {/* Master routes */}
      <Route path="/auth" element={hostTenantSlug ? <TenantAuthPage /> : <AuthPage />} />
      <Route path="/auth/callback" element={<Navigate to="/auth" replace />} />
      <Route path="/auth/google" element={<GoogleAuthCallbackPage />} />
      <Route path="/app" element={<ProtectedRoute><PlayerApp /></ProtectedRoute>} />
      <Route path="/app/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
      <Route path="/app/explore/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/staff-portal" element={<ProtectedRoute><StaffPortalPage /></ProtectedRoute>} />

      {/* Referral staff — own dashboard after login.
          Single canonical URL (no /:tenantSlug variant) — tenant context
          flows through the auth token. A tenant-prefixed variant conflicted
          with /admin/* because React Router ranks two concrete segments
          higher than a splat, so /admin/referral-staff would bounce here. */}
      <Route path="/referral-staff" element={<ProtectedRoute><ReferralRoleGuard><ReferralStaffDashboard /></ReferralRoleGuard></ProtectedRoute>} />

      {/* Public return route for payment gateways — does NOT require auth
          so the player never gets a blank screen even if mobile drops the
          session during the external gateway redirect. */}
      <Route path="/deposit-return" element={<DepositReturnPage />} />
      <Route path="/:tenantSlug/deposit-return" element={<DepositReturnPage />} />

      {/* Finance portal — fully isolated auth. No overlap with admin/player. */}
      <Route path="/finance/auth" element={<FinanceAuthPage />} />
      <Route path="/finance" element={<FinanceDashboard />} />

      {/* Public legal pages — no auth required */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/:tenantSlug/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/:tenantSlug/terms" element={<TermsOfServicePage />} />
      
      {/* Tenant routes */}
      <Route path="/:tenantSlug/auth" element={<TenantAuthPage />} />
      <Route path="/:tenantSlug/auth/google" element={<GoogleAuthCallbackPage />} />
      <Route path="/:tenantSlug/app" element={<ProtectedRoute><PlayerApp /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/roulette" element={<ProtectedRoute><RoulettePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      
      {/* Bare tenant URL: /morpheus → redirect to /morpheus/auth */}
      <Route path="/:tenantSlug" element={<TenantRootRedirect />} />
      
      {/* Root */}
      <Route path="/" element={agentHost ? <Navigate to="/referral-staff" replace /> : (hostTenantSlug ? <TenantRootRedirect tenantSlugOverride={hostTenantSlug} /> : <Navigate to="/auth" replace />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function GoogleAnalyticsRouteTracker() {
  const location = useLocation();
  const { user, tenantSlug } = useAuth();

  useEffect(() => {
    setAnalyticsUser(user, tenantSlug);
  }, [user, tenantSlug]);

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`, document.title);
  }, [location.pathname, location.search]);

  return null;
}

function InstallPromptGate() {
  const location = useLocation();
  const playerAppPath = /\/app(?:\/|$)/.test(location.pathname);

  return playerAppPath ? null : <InstallPrompt />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <GoogleAnalyticsRouteTracker />
          <AppRouter />
          <TenantManifestLink />
          <InstallPromptGate />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                background: '#131822',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff'
              }
            }}
          />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
